import { Component, type ErrorInfo, type ReactNode } from "react";
import { isChunkError, retryFailedChunks } from "../chunk";
import { navigate } from "../router";
import { t } from "../i18n";

// What a page that threw draws instead of nothing: without it one error
// unmounts the whole app, a blank pane that going back cannot mend, as the
// router is the app's own. It says what happened and offers a reload, and
// is cleared when the path changes (`resetKey`), so another page draws as
// it should.

type Props = { resetKey?: string; children: ReactNode };
type State = { error: unknown; key?: string };

export class PageBoundary extends Component<Props, State> {
  override state: State = { error: null, key: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey === state.key) return null;
    // another page: start over, a part that failed asked for again (chunk.ts)
    retryFailedChunks();
    return { error: null, key: props.resetKey };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("share: a page failed", error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const stale = isChunkError(error);
    return (
      <main className="narrow card page-error" role="alert">
        <h2>{stale ? t("common:pageStaleTitle") : t("common:pageErrorTitle")}</h2>
        <p className="muted">{stale ? t("common:pageStaleText") : t("common:pageErrorText")}</p>
        {!stale && <pre className="page-error-detail">{error instanceof Error ? error.message : String(error)}</pre>}
        <div className="row">
          <button className="primary" onClick={() => window.location.reload()}>
            {t("common:reload")}
          </button>
          <button onClick={() => navigate("/shares")}>{t("common:backToShares")}</button>
        </div>
      </main>
    );
  }
}
